import { validationErrorMessage } from 'common/constants';
import {
  ATBeaconButton,
  ATBeaconControl,
  ATBeaconSegmentedControl,
  ATBeaconSegmentedControlOption,
} from 'components/common/beacon/beaconRegistration';
import React, { useRef } from 'react';
import { HoldingTransferType } from 'utils/constants';
import { CTAVariants } from '@trp-beacon-ui-kit/foundation/dist/_common/types/cta-variant';
import { Holding } from '../../../../../api/atv/atvApi.types';
import './PartialTransferHolding.scss';

interface PartialTransferHoldingProps {
  holding: any;
  selectedHolding: Holding | null;
  isManualEntry: boolean;
  updateSelectedHoldings: (
    holding: any,
    action: 'add' | 'remove' | 'update',
    updateVal?: any,
  ) => void;
  setNotificationMessage: (message: string) => void;
}

const PartialTransferHolding: React.FC<PartialTransferHoldingProps> = ({
  holding,
  selectedHolding,
  isManualEntry,
  updateSelectedHoldings,
  setNotificationMessage,
}) => {
  const amountRef = useRef<HTMLInputElement>(null);
  const symbolRef = useRef<HTMLInputElement>(null);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value !== '') {
      const parsedValue = parseFloat(value);
      updateSelectedHoldings(selectedHolding, 'update', { quantity: parsedValue });
    } else {
      updateSelectedHoldings(selectedHolding, 'update', { quantity: '' });
    }

    validate();
  };

  const handleSymbolChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    updateSelectedHoldings(selectedHolding, 'update', { symbol: value });
  };

  const handleTransferTypeChange = (event: any) => {
    updateSelectedHoldings(selectedHolding, 'update', {
      transferType: event.detail.value,
      quantity: isManualEntry ? 0 : holding.quantity,
    });

    resetForm();
    validate();
  };

  const handleRemoveOrRestoreHolding = () => {
    if (selectedHolding) {
      resetForm();
      validate();
    }

    if (isManualEntry) {
      updateSelectedHoldings(holding, 'update', { restored: !holding.restored });
    } else {
      updateSelectedHoldings(holding, selectedHolding ? 'remove' : 'add');
    }
  };

  const validate = () => {
    if (amountRef.current && !isManualEntry) {
      const amountValue = amountRef.current.value.trim();
      if (!amountValue) {
        setNotificationMessage(validationErrorMessage.ZERO_QUANTITY);
      } else if (parseFloat(amountValue) > holding.quantity) {
        setNotificationMessage(
          'Please update the amount of shares to an amount equal to or less than the available shares.',
        );
      }
      amountRef.current.setAttribute(
        'aria-invalid',
        (!amountRef.current.checkValidity()).toString(),
      );
    }
  };

  const resetForm = () => {
    if (amountRef.current) {
      amountRef.current.value = holding.quantity;
    }
    if (symbolRef.current) {
      symbolRef.current.value = holding.symbol;
    }
  };

  let isRemoved = false;

  if (isManualEntry) {
    if (!holding.restored) {
      isRemoved = true;
    }
  } else if (!selectedHolding) {
    isRemoved = true;
  }

  return (
    <div className="holding-container">
      <div className="holding-container-top">
        <ATBeaconControl>
          <label slot="label" htmlFor={`asset-input-${holding.id}`}>
            {isManualEntry ? (
              <>
                Your ticker,
                <br />
                &quot;Cash,&quot; or CUSIP
              </>
            ) : (
              'Asset'
            )}
          </label>
          {!isManualEntry ? (
            <input
              type="text"
              name={`asset-input-${holding.id}`}
              defaultValue={holding.symbol}
              id={`asset-input-${holding.id}`}
              data-testid={`asset-input-${holding.id}`}
              aria-invalid="false"
              readOnly
            />
          ) : (
            <input
              type="text"
              name={`asset-input-${holding.id}`}
              ref={symbolRef}
              defaultValue={holding.symbol}
              id={`asset-input-${holding.id}`}
              data-testid={`asset-input-${holding.id}`}
              aria-invalid="false"
              onChange={handleSymbolChange}
              required
              disabled={isRemoved}
              aria-disabled={isRemoved}
            />
          )}
          {!isManualEntry && (
            <div className="b-typography-sans-serif--caption--200" slot="hint">
              Ticker, “Cash,” or CUSIP
            </div>
          )}
        </ATBeaconControl>
        <ATBeaconSegmentedControl
          className="text-center-left-on-mobile"
          onBeaconEventInput={handleTransferTypeChange}
          disabled={isRemoved}
          aria-disabled={isRemoved}
        >
          <label slot="title" htmlFor={`transferType-${holding.id}`}>
            Transfer Type
          </label>
          <ATBeaconSegmentedControlOption
            data-testid={`total-${holding.id}`}
            value={HoldingTransferType.TOTAL}
          >
            Total
          </ATBeaconSegmentedControlOption>
          <ATBeaconSegmentedControlOption
            data-testid={`partial-${holding.id}`}
            value={HoldingTransferType.PARTIAL}
          >
            Partial
          </ATBeaconSegmentedControlOption>
        </ATBeaconSegmentedControl>
        <ATBeaconControl stepper-variant="none" className="field-amount">
          <label
            slot="label"
            id={`amount-label-${holding.id}`}
            htmlFor={`amount-input-${holding.id}`}
          >
            Amount of Shares
          </label>
          <input
            type="number"
            name="amount"
            ref={amountRef}
            value={selectedHolding?.quantity ?? holding.quantity}
            onChange={handleAmountChange}
            id={`amount-input-${holding.id}`}
            data-testid={`amount-input-${holding.id}`}
            aria-invalid="false"
            aria-live="polite"
            readOnly={
              selectedHolding?.transferType === HoldingTransferType.TOTAL &&
              !isManualEntry
            }
            required
            min={0}
            max={!isManualEntry ? holding.quantity : undefined}
            step="any"
            disabled={isRemoved}
            aria-disabled={isRemoved}
          />
          {!isManualEntry && (
            <div className="b-typography-sans-serif--caption--200" slot="hint">
              {holding.quantity} shares available
            </div>
          )}
        </ATBeaconControl>
      </div>
      <div className="divider"></div>
      <ATBeaconButton
        variant={CTAVariants.Tertiary}
        onClick={handleRemoveOrRestoreHolding}
      >
        {!isRemoved ? 'Remove Holding' : 'Restore Holding'}
      </ATBeaconButton>
    </div>
  );
};

export default PartialTransferHolding;
